import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './carousel.css';
import { ReactComponent as LeftBlack } from '../../../assets/icons/trvto_left-arrow.svg';
import { ReactComponent as RightBlack } from '../../../assets/icons/trvto_right-arrow.svg';
import { useTempOrg } from '../../../context/TempOrgContext';

export default function Carousel({ customClass, imageData = [], videoData = [], TripCardClick, packId }) {
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { tempOrg } = useTempOrg() || {};
    const intervalRef = useRef(null);
    const carouselRef = useRef(null);
    const location = useLocation();

    const path = location.pathname;
    const isPageEligible = path === '/' || path.startsWith('/search');

    const combinedData = [];

    if (imageData.length > 0) {
        combinedData.push({ type: 'image', src: imageData[0] }); // First image
    }

    if (videoData.length > 0) {
        combinedData.push({ type: 'video', src: videoData[0] }); // Video as 2nd slide
    }

    if (imageData.length > 1) {
        imageData.slice(1).forEach((src) => combinedData.push({ type: 'image', src })); // Remaining images
    }

    // Update isMobile on window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Observe visibility (100% in view)
    useEffect(() => {
        const currentRef = carouselRef.current; // ✅ store current value

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { threshold: 1.0 }
        );

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    // Only scroll on eligible page AND based on platform
    const shouldAutoScroll = isPageEligible && ((isMobile && isVisible) || (!isMobile && isHovered));

    // Auto-scroll logic
    useEffect(() => {
        if (shouldAutoScroll) {
            intervalRef.current = setInterval(() => {
                setCarouselIndex((prevIndex) => (prevIndex + 1) % combinedData.length);
            }, 2500);
        } else {
            clearInterval(intervalRef.current);
        }

        return () => clearInterval(intervalRef.current);
    }, [shouldAutoScroll, combinedData.length]);
    useEffect(() => {
        // Select videos with either class
        const videos = document.querySelectorAll('.carouselVideo, .carouselVideoHidden');

        videos.forEach((v, i) => {
            if (i === carouselIndex) {
                v.play().catch(() => {}); // Play only active video
            } else {
                v.pause(); // Pause inactive videos
                v.currentTime = 0; // Reset to start
            }
        });
    }, [carouselIndex]);

    // Manual controls
    const carouselPrev = () => {
        setCarouselIndex((prev) => (prev === 0 ? combinedData.length - 1 : prev - 1));
    };

    const carouselNext = () => {
        setCarouselIndex((prev) => (prev + 1) % combinedData.length);
    };

    return (
        <div
            ref={carouselRef}
            className={`carousel ${customClass}`}
            style={{ marginTop: tempOrg?.id ? '60px' : '' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <button className='carouselPrev' onClick={carouselPrev}>
                <LeftBlack className='carouselRightArrow' />
            </button>

            {combinedData.map((item, index) => (
                <>
                    {item.type === 'video' ? (
                        <video
                            src={item.src}
                            className={index === carouselIndex ? 'carouselVideo' : 'carouselVideoHidden'}
                            controls
                            muted
                            playsInline
                        />
                    ) : (
                        <img
                            key={index}
                            src={item.src}
                            alt={`Carousel ${index + 1}`}
                            className={index === carouselIndex ? 'carouselImg' : 'carouselImgHidden'}
                            onClick={() => TripCardClick(packId)}
                            loading='lazy'
                            decoding='async'
                        />
                    )}
                </>
            ))}

            <button className='carouselNext' onClick={carouselNext}>
                <RightBlack className='carouselRightArrow' />
            </button>

            <span className='carouselIndicators'>
                {combinedData.map((_, index) => (
                    <div
                        key={index}
                        className={carouselIndex === index ? 'carouselIndicator' : 'carouselIndicatorTrs'}
                    ></div>
                ))}
            </span>
        </div>
    );
}
